import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type RetentionInput,
  type RetentionSummary,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { HelpTip } from '../components/HelpTip';
import { NumberField } from '../components/NumberField';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError, toFieldErrors } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import styles from './ProjectScreen.module.css';

/** Must match the backend RETENTION_BOUNDS (app/src/orgs/retention.bounds.ts). */
const BOUNDS = {
  evidenceDays: { min: 1, max: 365 },
  executionHistoryMonths: { min: 1, max: 36 },
  journeyHealthWindowDays: { min: 1, max: 365 },
} as const;

type FieldKey = keyof typeof BOUNDS;
type NumOrEmpty = number | '';

interface FieldErrors {
  evidenceDays?: string;
  executionHistoryMonths?: string;
  journeyHealthWindowDays?: string;
}

interface FormSummary {
  message: string;
  requestId?: string;
}

type LoadState = 'loading' | 'error' | 'ready';

function clientError(key: FieldKey, value: NumOrEmpty): string | undefined {
  const { min, max } = BOUNDS[key];
  if (value === '') return 'Enter a value.';
  if (!Number.isInteger(value) || value < min || value > max) {
    return `Enter a whole number between ${min} and ${max}.`;
  }
  return undefined;
}

export interface RetentionSettingsProps {
  client?: ApiClient;
}

export function RetentionSettings({ client = defaultClient }: RetentionSettingsProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  useFocusMain();

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [evidenceDays, setEvidenceDays] = useState<NumOrEmpty>('');
  const [executionHistoryMonths, setExecutionHistoryMonths] = useState<NumOrEmpty>('');
  const [journeyHealthWindowDays, setJourneyHealthWindowDays] = useState<NumOrEmpty>('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [summary, setSummary] = useState<FormSummary | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const apply = (r: RetentionSummary) => {
    setEvidenceDays(r.evidenceDays);
    setExecutionHistoryMonths(r.executionHistoryMonths);
    setJourneyHealthWindowDays(r.journeyHealthWindowDays);
  };

  const load = useCallback(async () => {
    if (!orgId) return;
    setPhase('loading');
    try {
      apply(await client.getRetention(orgId));
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || !orgId) return;

    const errors: FieldErrors = {};
    const ev = clientError('evidenceDays', evidenceDays);
    const ex = clientError('executionHistoryMonths', executionHistoryMonths);
    const jh = clientError('journeyHealthWindowDays', journeyHealthWindowDays);
    if (ev) errors.evidenceDays = ev;
    if (ex) errors.executionHistoryMonths = ex;
    if (jh) errors.journeyHealthWindowDays = jh;
    if (errors.evidenceDays || errors.executionHistoryMonths || errors.journeyHealthWindowDays) {
      setFieldErrors(errors);
      setSuccess(null);
      setSummary({ message: 'Please fix the errors below and try again.' });
      return;
    }

    setFieldErrors({});
    setSummary(null);
    setSuccess(null);
    setBusy(true);
    try {
      const input: RetentionInput = {
        evidenceDays: evidenceDays as number,
        executionHistoryMonths: executionHistoryMonths as number,
        journeyHealthWindowDays: journeyHealthWindowDays as number,
      };
      apply(await client.updateRetention(orgId, input));
      setSuccess('Retention settings saved.');
      // Success uses a polite live region; move focus there for AT users.
      requestAnimationFrame(() => successRef.current?.focus());
    } catch (err) {
      const fields = toFieldErrors(err);
      const mapped: FieldErrors = {};
      if (fields.evidenceDays) mapped.evidenceDays = fields.evidenceDays;
      if (fields.executionHistoryMonths) mapped.executionHistoryMonths = fields.executionHistoryMonths;
      if (fields.journeyHealthWindowDays) mapped.journeyHealthWindowDays = fields.journeyHealthWindowDays;
      if (mapped.evidenceDays || mapped.executionHistoryMonths || mapped.journeyHealthWindowDays) {
        setFieldErrors(mapped);
        setSummary({ message: 'Please fix the errors below and try again.' });
      } else {
        const display = toDisplayError(err);
        setSummary({
          message: display.message,
          ...(display.requestId ? { requestId: display.requestId } : {}),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 className={styles.heading}>Retention settings</h1>
        <p className={styles.subheading}>
          Control how long we keep evidence and history.{' '}
          <HelpTip term="evidenceRetention" />
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading settings…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' ? (
          <form onSubmit={submit} noValidate>
            <div className={styles.summary}>
              <InlineAlert
                ref={summaryRef}
                severity="error"
                {...(summary?.requestId ? { requestId: summary.requestId } : {})}
              >
                {summary?.message}
              </InlineAlert>
            </div>

            <InlineAlert ref={successRef} severity="success">
              {success}
            </InlineAlert>

            <NumberField
              label="Evidence retention (days)"
              id="evidence-days"
              value={evidenceDays}
              onChange={setEvidenceDays}
              min={BOUNDS.evidenceDays.min}
              max={BOUNDS.evidenceDays.max}
              helpText="1–365 days."
              {...(fieldErrors.evidenceDays ? { error: fieldErrors.evidenceDays } : {})}
            />

            <NumberField
              label="Execution & review history (months)"
              id="execution-history-months"
              value={executionHistoryMonths}
              onChange={setExecutionHistoryMonths}
              min={BOUNDS.executionHistoryMonths.min}
              max={BOUNDS.executionHistoryMonths.max}
              helpText="1–36 months."
              {...(fieldErrors.executionHistoryMonths
                ? { error: fieldErrors.executionHistoryMonths }
                : {})}
            />

            <NumberField
              label="Journey-health window (days)"
              id="journey-health-days"
              value={journeyHealthWindowDays}
              onChange={setJourneyHealthWindowDays}
              min={BOUNDS.journeyHealthWindowDays.min}
              max={BOUNDS.journeyHealthWindowDays.max}
              helpText="1–365 days."
              {...(fieldErrors.journeyHealthWindowDays
                ? { error: fieldErrors.journeyHealthWindowDays }
                : {})}
            />

            <div className={styles.actions}>
              <Button type="submit" busy={busy}>
                {busy ? 'Saving…' : 'Save settings'}
              </Button>
            </div>
          </form>
        ) : null}
      </Card>
    </AppShell>
  );
}
